import { GridContainer } from "@amzn/stencil-react-components/layout";
import { Col } from "@amzn/stencil-react-components/layout";
import { LinkTextDecorationOptions } from "@amzn/stencil-react-components/link";
import { Text } from "@amzn/stencil-react-components/text";
import { InternalLink } from "common/components/InternalLink/InternalLink";
import { TrackClickInteraction } from "common/helpers";
import { VoteHandler } from "common/hooks";
import { getString } from "common/uistringlabels/uiStringUtils";
import { AIForMeTool } from "lib/models/aiforme-models";
import { CatalogicalService } from "lib/services";
import { FeedbackModelRecord } from "lib/services/catalogical-models";
import React, { FC } from "react";

import { RecentlyUsedCard, RecentlyUsedTool } from "./RecentlyUsedCard";

/**
 * Props for the RecentlyUsedSection component
 */
export interface RecentlyUsedSectionProps {
  tools: RecentlyUsedTool[];
  voteHandler: VoteHandler;
  client: CatalogicalService<AIForMeTool>;
  /** User's previous votes/ratings keyed by taxonomy ID */
  userFeedbacks: Record<string, FeedbackModelRecord>;
  /** Callback to track click interactions when user clicks "Learn More" */
  onTrackClick: TrackClickInteraction;
}

/**
 * TODO: when "history page" is ready update this url to send the user to that page
 */
const CHECK_TOOLS_HISTORY_INTERNAL_LINK_URL = "/aiforme/allAiTools?";

/**
 * Maximum number of tools to display in the section
 */
const SHOW_ONLY_THREE_TOOLS = 3;

/**
 * Section component displaying recently used AI tools with rating capabilities
 * Shows up to 3 tools and provides a link to view full history
 * @param props - Component props
 * @returns Rendered section component
 */
export const RecentlyUsedSection: FC<RecentlyUsedSectionProps> = ({
  tools,
  voteHandler,
  client,
  userFeedbacks,
  onTrackClick,
}) => {
  return (
    <Col rowGap={24} alignItems="center" data-testid="recently-used-section">
      <Text fontSize="T400" fontWeight="bold" data-testid="recently-used-heading">
        {getString("aiForMe.landingPage.recentlyUsedToolsSection.sectionTitle")}
      </Text>

      <GridContainer>
        {tools.slice(0, SHOW_ONLY_THREE_TOOLS).map((tool, index) => (
          <RecentlyUsedCard
            key={tool.taxonomyId}
            tool={tool}
            index={index}
            voteHandler={voteHandler}
            client={client}
            userVote={userFeedbacks?.[tool.taxonomyId]}
            onTrackClick={onTrackClick}
            gridSpan={{ lg: 4, md: 4 }}
          />
        ))}
      </GridContainer>

      <Text fontSize="T200" color="var(--st-dt-color-text-secondary)" data-testid="recently-used-footer">
        {getString("aiForMe.landingPage.recentlyUsedToolsSection.checkToolsHistory")}
        <InternalLink to={CHECK_TOOLS_HISTORY_INTERNAL_LINK_URL} textDecoration={LinkTextDecorationOptions.Underline}>
          {getString("aiForMe.landingPage.recentlyUsedToolsSection.checkToolsHistoryAllUsedListLinkText")}
        </InternalLink>
      </Text>
    </Col>
  );
};
